import React, { useState, useEffect, useMemo } from "react";
import { Modal, Form, Button, Row, Col } from "react-bootstrap";
import { startOfWeek, addDays, addWeeks, format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";

const COLOR_OPTIONS = [
  { value: "red", bg: "#dc3545", label: "Red" },
  { value: "orange", bg: "#fd7e14", label: "Orange" },
  { value: "blue", bg: "#0d6efd", label: "Blue" },
  { value: "green", bg: "#198754", label: "Green" }
];

const TEAM_OPTIONS = [
  { value: "all", label: { en: "All Teams", pt: "Todos os Times" } },
  { value: "team1", label: { en: "Team 1", pt: "Time 1" } },
  { value: "team2", label: { en: "Team 2", pt: "Time 2" } },
  { value: "team3", label: { en: "Team 3", pt: "Time 3" } }
];

const toDateStr = (d) => format(d, "yyyy-MM-dd");
const today = () => new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

export default function NoticeModal({
  show,
  onHide,
  mode = "create", // "create" | "edit" | "view"
  noticeData,
  onSave,
  onDelete,
  language = "en",
  darkTheme,
  modalTheme,
  defaultTeam = "all"
}) {
  const readOnly = mode === "view";
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    color: "blue",
    date: "",
    team: "all"
  });
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    if ((mode === "edit" || mode === "view") && noticeData) {
      setFormData({
        title: noticeData.title || "",
        description: noticeData.description || "",
        color: noticeData.color || "blue",
        date: noticeData.date || "",
        team: noticeData.team || "all"
      });
      // Set weekOffset to match the notice date's week
      if (noticeData.date) {
        const noticeDate = new Date(noticeData.date + "T00:00:00");
        const todayMonday = startOfWeek(today(), { weekStartsOn: 1 });
        const noticeMon = startOfWeek(noticeDate, { weekStartsOn: 1 });
        const diff = Math.round((noticeMon - todayMonday) / (7 * 24 * 60 * 60 * 1000));
        setWeekOffset(diff);
      }
    } else if (mode === "create") {
      const todayStr = toDateStr(today());
      setFormData({
        title: "",
        description: "",
        color: "blue",
        date: todayStr,
        team: defaultTeam
      });
      setWeekOffset(0);
    }
  }, [mode, noticeData, show, defaultTeam]);

  const weekDays = useMemo(() => {
    const monday = addWeeks(startOfWeek(today(), { weekStartsOn: 1 }), weekOffset);
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(monday, i);
      return { date: d, str: toDateStr(d) };
    });
  }, [weekOffset]);

  const weekLabel = useMemo(() => {
    const locale = language === "pt" ? ptBR : enUS;
    const mon = weekDays[0].date;
    const sun = weekDays[6].date;
    return `${format(mon, "dd MMM", { locale })} - ${format(sun, "dd MMM yyyy", { locale })}`;
  }, [weekDays, language]);

  const handleSave = () => {
    if (!formData.title.trim() || !formData.date) return;
    onSave(formData);
  };

  const isValid = formData.title.trim() && formData.date;
  const locale = language === "pt" ? ptBR : enUS;
  const dayNames = language === "pt"
    ? ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"]
    : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <Modal show={show} onHide={onHide} centered animation={false} data-bs-theme={modalTheme}>
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === "view"
            ? (language === "en" ? "Notice" : "Aviso")
            : mode === "edit"
              ? (language === "en" ? "Edit Notice" : "Editar Aviso")
              : (language === "en" ? "New Notice" : "Novo Aviso")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>{language === "en" ? "Title" : "Título"}{!readOnly ? " *" : ""}</Form.Label>
          <Form.Control
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder={language === "en" ? "e.g. Holiday, Meeting..." : "ex: Feriado, Reunião..."}
            autoFocus={!readOnly}
            readOnly={readOnly}
            plaintext={readOnly}
          />
        </Form.Group>

        {!readOnly && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>{language === "en" ? "Color" : "Cor"}</Form.Label>
              <div className="d-flex gap-2">
                {COLOR_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: opt.value })}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      backgroundColor: opt.bg,
                      border: formData.color === opt.value
                        ? "3px solid #fff"
                        : "3px solid transparent",
                      boxShadow: formData.color === opt.value
                        ? `0 0 0 2px ${opt.bg}`
                        : "none",
                      cursor: "pointer",
                      transition: "all 0.15s ease"
                    }}
                    title={opt.label}
                  />
                ))}
              </div>
            </Form.Group>

            {/* Week date picker */}
            <Form.Group className="mb-3">
              <Form.Label>{language === "en" ? "Date" : "Data"} *</Form.Label>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setWeekOffset(w => w - 1)}
                  style={{ width: 36, height: 36, padding: 0, fontSize: '1.1rem' }}
                >
                  ‹
                </button>
                <span style={{ fontWeight: 600, fontSize: '0.85rem', textTransform: 'capitalize' }}>
                  {weekLabel}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setWeekOffset(w => w + 1)}
                  style={{ width: 36, height: 36, padding: 0, fontSize: '1.1rem' }}
                >
                  ›
                </button>
              </div>
              <div className="d-flex gap-1">
                {weekDays.map((day, i) => {
                  const isSelected = formData.date === day.str;
                  const isToday = day.str === toDateStr(today());
                  return (
                    <button
                      key={day.str}
                      type="button"
                      onClick={() => setFormData({ ...formData, date: day.str })}
                      style={{
                        flex: 1,
                        padding: '6px 2px',
                        borderRadius: 8,
                        border: isSelected
                          ? '2px solid #3b82f6'
                          : isToday
                            ? '2px solid #64748b'
                            : '2px solid transparent',
                        backgroundColor: isSelected ? '#3b82f6' : 'transparent',
                        color: isSelected ? '#fff' : 'inherit',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.15s ease',
                        fontWeight: isSelected ? 700 : 500
                      }}
                    >
                      <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', opacity: 0.7 }}>
                        {dayNames[i]}
                      </div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                        {day.date.getDate()}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{language === "en" ? "Team" : "Time"}</Form.Label>
              <Form.Select
                value={formData.team}
                onChange={(e) => setFormData({ ...formData, team: e.target.value })}
              >
                {TEAM_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label[language] || opt.label.en}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </>
        )}

        <Form.Group className="mb-3">
          <Form.Label>{language === "en" ? "Description" : "Descrição"}{!readOnly ? ` (${language === "en" ? "optional" : "opcional"})` : ""}</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={readOnly && !formData.description ? (language === "en" ? "No description" : "Sem descrição") : (language === "en" ? "Additional details..." : "Detalhes adicionais...")}
            readOnly={readOnly}
            plaintext={readOnly}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        {readOnly ? (
          <Button variant="secondary" onClick={onHide}>
            {language === "en" ? "Close" : "Fechar"}
          </Button>
        ) : (
          <>
            {mode === "edit" && (
              <Button
                variant="outline-danger"
                className="me-auto"
                onClick={onDelete}
              >
                {language === "en" ? "Delete" : "Excluir"}
              </Button>
            )}
            <Button variant="secondary" onClick={onHide}>
              {language === "en" ? "Cancel" : "Cancelar"}
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={!isValid}>
              {language === "en" ? "Save" : "Salvar"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
